import React, { useEffect, useState } from 'react';
import { DiaryProvider, useDiary, ACTIONS } from '../DiaryV2/DiaryContext';
import { DragProvider } from '../DiaryV2/DragContext';
import DiaryViewport from '../DiaryV2/DiaryViewport';
import { normalizeDiaryResponse, calculateTimeBounds } from '../DiaryV2/normalizeData';
import { hairdresserService } from '../../_services/hairdresser.service';

/**
 * The REAL diary, hosted in the conversation.
 *
 * This replaces a lane renderer I wrote by hand, which was the same mistake I
 * had already named twice today: hand-rolling a chart when Finance had one,
 * and nearly rebuilding the loyalty designer in a chat bubble. A second diary
 * is worse than no diary. It drifts from the one people actually use, it has
 * none of its behaviour, and every improvement has to be made twice.
 *
 * So the card is a host and nothing more. DiaryViewport draws staff as
 * columns with time down the side and scrolls horizontally through the team,
 * exactly as the diary screen does, with the product's own blocks, colours,
 * overlap layout, non-working overlay and now-indicator.
 *
 * It is READ-ONLY here on purpose. The provider's interaction callbacks are
 * left unset, so nothing in the card can drag, resize or open a booking. The
 * diary screen owns those, and a half-interactive copy is how you get someone
 * dragging an appointment in a chat and wondering why the client was not
 * told.
 */

function DiaryLoader({ date, onState }) {
    const { dispatch } = useDiary();

    useEffect(() => {
        let alive = true;
        onState({ loading: true, error: null });

        hairdresserService.getDiaryData({ startDate: date, endDate: date })
            .then((res) => {
                if (!alive) return;
                const data = (res && res.data && (res.data.responseData || res.data.data)) || null;
                if (!data) {
                    onState({ loading: false, error: 'empty' });
                    return;
                }

                // Same normaliser the diary screen uses, on the same payload.
                const { staffMembers, eventsByStaff } = normalizeDiaryResponse(data, date);

                // Honour the salon's saved column order when the API sends it,
                // so the team appears in the order they arranged, not the order
                // the database happened to return.
                let ordered = staffMembers;
                const saved = data.staffOrder;
                if (saved && saved.length) {
                    const at = {};
                    saved.forEach((id, i) => { at[id] = i; });
                    ordered = [...staffMembers].sort(
                        (a, b) => (at[a.id] == null ? 999 : at[a.id]) - (at[b.id] == null ? 999 : at[b.id]));
                }

                dispatch({ type: ACTIONS.SET_STAFF_ORDER, payload: ordered.map((s) => s.id) });
                dispatch({ type: ACTIONS.SET_TIME_BOUNDS, payload: calculateTimeBounds(ordered) });
                ordered.forEach((s) => {
                    dispatch({
                        type: ACTIONS.SET_STAFF_DATA,
                        payload: { staffId: s.id, staff: s, events: (eventsByStaff && eventsByStaff[s.id]) || [] }
                    });
                });
                dispatch({ type: ACTIONS.SET_INITIAL_LOAD_COMPLETE });
                onState({ loading: false, error: null, staffCount: ordered.length });
            })
            .catch(() => {
                if (alive) onState({ loading: false, error: 'failed' });
            });

        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    return <DiaryViewport />;
}

export default function DiaryEmbed({ date }) {
    const [state, setState] = useState({ loading: true, error: null });

    return (
        <div className="agt-diaryembed">
            {state.loading && <p className="agt-diaryembed-note">Opening the diary…</p>}
            {state.error && (
                <p className="agt-diaryembed-note">
                    {state.error === 'failed'
                        ? 'Couldn’t load the diary just now.'
                        : 'Nothing to show for that day.'}
                </p>
            )}
            {/* Mounted even while loading so the viewport measures itself
                against its real width rather than zero. */}
            <div className="agt-diaryembed-stage" hidden={!!state.error}>
                <DiaryProvider initialDate={date}>
                    {/* StaffColumn/EventBlock call useDrag() unconditionally,
                        so the provider is required; readOnly keeps it inert. */}
                    <DragProvider readOnly>
                        <DiaryLoader date={date} onState={setState} />
                    </DragProvider>
                </DiaryProvider>
            </div>
        </div>
    );
}
